import {
  Box,
  IconButton,
  CardActions,
  Card as MuiCard,
  CardActionsProps,
} from "@mui/material";
import { Icon } from "../Icon";
import { forwardRef } from "react";
import { CardImage, CardImageProps } from "../CardImage";
import { COLOR_CONSTANTS } from "../../constants/styles";
import { CardHeader } from "../CardHeader";

/**
 * 작은 썸네일 + 북마크 토글 카드 — 목록(grid) 용 (120×112px 고정).
 * 드래그/전체화면이 필요하면 {@link Card} 또는 {@link FloatImageViewer} 를 쓴다.
 */
export interface ImageCardProps {
  fileName: string;
  bookmarked?: boolean;
  selected?: boolean;
  onClickImage?: (fileName: string) => void;
  onToggleBookmark?: () => void;
  image?: string;
  alt?: string;
  disabled?: boolean;
  /** 썸네일 `<img>` 의 로드 방식·CORS 모드 — 목록처럼 카드가 많은 호출부가 넘긴다 */
  imageProps?: Pick<CardImageProps, "loading" | "crossOrigin">;
  cardActionsProps?: Omit<CardActionsProps, "children">;
}

export const ImageCard = forwardRef<HTMLDivElement, ImageCardProps>(
  (
    {
      fileName,
      bookmarked = false,
      selected = false,
      onClickImage,
      onToggleBookmark,
      image,
      alt,
      disabled = false,
      imageProps,
      cardActionsProps,
    },
    ref
  ) => {
    return (
      <MuiCard
        ref={ref}
        sx={{
          ...card,
          cursor: "pointer",
          // 사진이 오기 전에도 자리를 잡아 둔다 — 높이를 사진이 정하면 목록이 밀린다
          height: "112px",
          width: "120px",
          outline: selected
            ? `2px solid ${COLOR_CONSTANTS.primary.main}`
            : "none",
        }}
      >
        <CardHeader
          size="small"
          fileName={fileName}
          sx={{ position: "absolute" }}
        />
        {/* 사진 고르기는 버튼이다 — img 에 onClick 만 달면 키보드로 도달·실행할 수 없다 */}
        <Box
          component="button"
          type="button"
          aria-label={fileName}
          aria-pressed={selected}
          disabled={disabled}
          onClick={() => onClickImage?.(fileName)}
          sx={imageButton}
        >
          <CardImage
            size="small"
            image={image}
            alt={alt}
            disabled={disabled}
            {...imageProps}
          />
        </Box>
        <CardActions sx={cardActions} {...cardActionsProps}>
          <IconButton size="small" onClick={onToggleBookmark}>
            {bookmarked ? (
              <Icon name="etc/BookmarkActive" size={24} clickable />
            ) : (
              <Icon name="etc/Bookmark" size={24} sx={bookmarkIcon} clickable />
            )}
          </IconButton>
        </CardActions>
      </MuiCard>
    );
  }
);

ImageCard.displayName = "ImageCard";

const imageButton = {
  display: "block",
  width: "100%",
  height: "100%",
  padding: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  "&:disabled": { cursor: "default" },
} as const;

const bookmarkIcon = {
  "& path": {
    fill: "black",
    stroke: "white",
    strokeWidth: 0.8,
    strokeOpacity: 1,
  },
} as const;

const card = {
  position: "relative",
  overflow: "hidden",
} as const;

const cardActions = {
  width: "100%",
  display: "flex",
  justifyContent: "flex-end",
  position: "absolute",
  bottom: 0,
  left: 0,
  padding: "2px",
} as const;
